import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";

import {
  deleteGroup,
  fetchGroup,
  fetchGroupMembers,
  removeGroupMembers,
  type GroupMemberItem,
} from "../../api/groups";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ApiError } from "../../lib/api";
import {
  updateTarget,
  type TargetListItem,
  type TargetWriteBody,
} from "../../api/targets";
import { AddToGroup } from "./AddToGroup";
import { ScrollableTable } from "../../components/ScrollableTable";
import { Sheet } from "../../components/Sheet";
import { Button } from "../../components/ui/Button";
import { Checkbox } from "../../components/ui/Checkbox";
import { SortHeader } from "../../components/ui/SortHeader";
import { TargetDrawer } from "../Targets/Drawer";

type SortKey = "full_name" | "primary_phone_e164" | "role" | "department";
type SortDir = "asc" | "desc";

const SORT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "full_name", label: "Name" },
  { key: "primary_phone_e164", label: "Phone" },
  { key: "role", label: "Role" },
  { key: "department", label: "Department" },
];

// Shared member-table track (checkbox + Name + Phone + Role + Dept + edit) and
// the min-width that keeps checkbox/Name/Phone usable while Role/Dept scroll.
const MEMBER_GRID = "28px 1.4fr 1.2fr 1fr 1fr 36px";
const MEMBER_TABLE_MIN_WIDTH = 596;

export function GroupDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => fetchGroup(id),
    enabled: !!id,
  });
  const membersQuery = useQuery({
    queryKey: ["groups", id, "members"],
    queryFn: () => fetchGroupMembers(id),
    enabled: !!id,
  });

  const members = useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data]);
  const existingIds = useMemo(() => new Set(members.map((m) => m.target_id)), [members]);

  // ─── Sort state ──────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState<SortKey>("full_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sortedMembers = useMemo(() => {
    const copy = [...members];
    copy.sort((a, b) => {
      const av = (a[sortKey] ?? "").toLowerCase();
      const bv = (b[sortKey] ?? "").toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [members, sortKey, sortDir]);

  // ─── Remove-selection state ──────────────────────────────────────────────────
  const [selectedToRemove, setSelectedToRemove] = useState<Set<string>>(new Set());

  function toggleRemove(targetId: string) {
    setSelectedToRemove((prev) => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  }

  const removeMut = useMutation({
    mutationFn: (targetIds: string[]) => removeGroupMembers(id, targetIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["groups", id, "members"] });
      void qc.invalidateQueries({ queryKey: ["groups", id] });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      setSelectedToRemove(new Set());
    },
  });

  // ─── Delete-group state ───────────────────────────────────────────────────────
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => deleteGroup(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      navigate("/groups");
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : "Failed to delete group."),
  });

  // ─── Add-picker state ────────────────────────────────────────────────────────
  const [showAdd, setShowAdd] = useState(false);

  // ─── In-group member edit ────────────────────────────────────────────────────
  // The drawer reuses <TargetDrawer>, which expects a TargetListItem. Member
  // rows are slim (no email/test_only/phone_verified), so we synthesize a
  // TargetListItem from the row; only the fields the drawer surfaces are edited.
  const [editing, setEditing] = useState<GroupMemberItem | null>(null);

  const editTarget = useMemo<TargetListItem | null>(() => {
    if (!editing) return null;
    return {
      target_id: editing.target_id,
      full_name: editing.full_name,
      first_name: null,
      last_name: null,
      primary_phone_e164: editing.primary_phone_e164,
      primary_email: null,
      role: editing.role,
      department: editing.department,
      test_only: false,
      phone_verified: false,
      phone_verified_at: null,
      group_ids: [],
      group_names: [],
    };
  }, [editing]);

  const editMut = useMutation({
    mutationFn: (vars: { id: string; patch: Partial<TargetWriteBody> }) =>
      updateTarget(vars.id, vars.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["groups", id, "members"] });
      void qc.invalidateQueries({ queryKey: ["targets"] });
      setEditing(null);
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <button
        onClick={() => navigate("/groups")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "var(--text-muted)",
          fontSize: 11.5,
          cursor: "pointer",
          padding: 0,
          alignSelf: "flex-start",
        }}
      >
        <ArrowLeft size={13} strokeWidth={1.8} />
        All groups
      </button>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 26,
            fontWeight: 600,
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          {groupQuery.data?.name ?? "Group"}
        </h1>
        <span
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          · {members.length} member{members.length === 1 ? "" : "s"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid var(--error)",
            background: "transparent",
            color: "var(--error)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Delete group
        </button>
      </div>
      {groupQuery.data?.description && (
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-secondary)", maxWidth: 640 }}>
          {groupQuery.data.description}
        </p>
      )}

      {/* Member toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Button
          variant={showAdd ? "secondary" : "primary"}
          onClick={() => setShowAdd((v) => !v)}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid var(--accent)",
            background: showAdd ? "transparent" : "var(--accent)",
            color: showAdd ? "var(--accent)" : "white",
            whiteSpace: "nowrap",
          }}
        >
          {showAdd ? "Close picker" : "+ Add members"}
        </Button>
        <div style={{ flex: 1 }} />
        <Button
          variant="danger"
          disabled={selectedToRemove.size === 0 || removeMut.isPending}
          onClick={() => removeMut.mutate([...selectedToRemove])}
          style={{
            padding: "7px 14px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            border: `1px solid ${selectedToRemove.size === 0 ? "var(--border)" : "var(--danger)"}`,
            background: "transparent",
            color: selectedToRemove.size === 0 ? "var(--text-muted)" : "var(--danger)",
            whiteSpace: "nowrap",
          }}
        >
          {removeMut.isPending
            ? "Removing…"
            : `Remove selected${selectedToRemove.size ? ` (${selectedToRemove.size})` : ""}`}
        </Button>
      </div>

      {removeMut.error && (
        <div style={{ fontSize: 11, color: "var(--error)", fontFamily: "var(--font-mono)" }}>
          {removeMut.error instanceof Error ? removeMut.error.message : "Failed to remove members."}
        </div>
      )}

      {/* Add-to-group tabbed surface (csv / manual / existing).
          Unlike the old picker it stays open after an add (the toolbar toggle
          closes it) so an operator can add via several tabs in one sitting; the
          member table re-renders live via onChanged. */}
      {showAdd && (
        <AddToGroup
          groupId={id}
          existingIds={existingIds}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ["groups", id, "members"] });
            void qc.invalidateQueries({ queryKey: ["groups", id] });
            void qc.invalidateQueries({ queryKey: ["groups"] });
          }}
        />
      )}

      {/* Member table */}
      <div
        style={{
          background: "var(--bg-card)",
          backdropFilter: "blur(var(--glass-blur))",
          border: "1px solid var(--glass-border)",
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {membersQuery.isLoading ? (
          <>
            <MemberHeader sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <div style={{ padding: "24px 14px", textAlign: "center", fontSize: 11, color: "var(--text-muted)" }}>
              Loading…
            </div>
          </>
        ) : sortedMembers.length === 0 ? (
          <>
            <MemberHeader sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <div style={{ padding: "24px 14px", textAlign: "center", fontSize: 11, color: "var(--text-muted)" }}>
              No members yet. Use Add members to pull targets into this group.
            </div>
          </>
        ) : (
          <ScrollableTable minWidth={MEMBER_TABLE_MIN_WIDTH}>
            <MemberHeader sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            {sortedMembers.map((m) => (
              <MemberRow
                key={m.target_id}
                member={m}
                checked={selectedToRemove.has(m.target_id)}
                onToggle={() => toggleRemove(m.target_id)}
                onEdit={() => setEditing(m)}
              />
            ))}
          </ScrollableTable>
        )}
      </div>

      {/* In-group member edit (writes through to the shared registry) */}
      <Sheet
        open={editing != null}
        onClose={() => setEditing(null)}
        title={editing?.full_name ?? "Member"}
      >
        {editTarget != null && (
          <TargetDrawer
            target={editTarget}
            onClose={() => setEditing(null)}
            onRequestDelete={() => {}}
            onSave={(patch) =>
              editMut.mutate(
                { id: editTarget.target_id, patch },
                {
                  onError: (err) =>
                    alert(err instanceof Error ? err.message : "Failed to update member."),
                },
              )
            }
            renderFooter={({ onCancel, onSave }) => (
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={onCancel} style={sheetCancelBtn()}>
                  cancel
                </button>
                <button onClick={onSave} disabled={editMut.isPending} style={sheetSaveBtn()}>
                  {editMut.isPending ? "saving…" : "save"}
                </button>
              </div>
            )}
          />
        )}
      </Sheet>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete group?"
        body="Members move to Ungrouped (not deleted). This cannot be undone."
        busy={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ─── Member table header (sortable) ───────────────────────────────────────────────

function MemberHeader({
  sortKey,
  sortDir,
  onSort,
}: {
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MEMBER_GRID,
        gap: 10,
        padding: "8px 14px",
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: "var(--text-muted)",
        fontWeight: 700,
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
      }}
    >
      <span />
      {SORT_COLUMNS.map(({ key, label }) => (
        <SortHeader
          key={key}
          label={label}
          active={sortKey === key}
          dir={sortDir}
          onClick={() => onSort(key)}
        />
      ))}
    </div>
  );
}

function MemberRow({
  member,
  checked,
  onToggle,
  onEdit,
}: {
  member: GroupMemberItem;
  checked: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MEMBER_GRID,
        gap: 10,
        padding: "9px 14px",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
        fontSize: 11,
        background: checked ? "var(--accent-subtle)" : "transparent",
      }}
    >
      <Checkbox checked={checked} onChange={onToggle} />
      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{member.full_name}</span>
      <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)", fontSize: 10.5 }}>
        {member.primary_phone_e164 ?? "—"}
      </span>
      <span style={{ color: "var(--text-muted)", fontSize: 10.5 }}>{member.role ?? "—"}</span>
      <span style={{ color: "var(--text-muted)", fontSize: 10.5 }}>{member.department ?? "—"}</span>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${member.full_name}`}
        title="Edit member"
        style={{
          display: "grid",
          placeItems: "center",
          width: 28,
          height: 28,
          borderRadius: 6,
          border: "1px solid var(--border-subtle)",
          background: "transparent",
          color: "var(--text-muted)",
          cursor: "pointer",
        }}
      >
        <Pencil size={13} strokeWidth={1.8} />
      </button>
    </div>
  );
}

// ─── Sheet footer buttons (mirrors Targets/Index.tsx) ──────────────────────────────

function sheetCancelBtn(): React.CSSProperties {
  return {
    padding: "8px 14px",
    borderRadius: 6,
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}

function sheetSaveBtn(): React.CSSProperties {
  return {
    padding: "8px 16px",
    borderRadius: 6,
    background: "var(--accent)",
    border: "1px solid var(--accent)",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    minHeight: "var(--tap-min)",
  };
}
